/**
 * Modal.jsx - Reusable modal dialog component.
 *
 * Renders a centered dialog over a semi-transparent backdrop.
 * Closes on backdrop click or Escape key.
 *
 * Props:
 *   title    {string}   - Heading displayed in the modal header.
 *   children {node}     - Body content (rendered inside .card-body).
 *   footer   {node}     - Optional footer area (buttons, actions).
 *   onClose  {function} - Called when the modal should close.
 *   narrow   {boolean}  - When true, caps the modal width at 440px instead of 600px.
 *   width    {string}   - "narrow" (440px) | "wide" (960px). Overrides the narrow prop.
 *
 * Usage:
 *   {showModal && (
 *     <Modal title="Confirm Delete" onClose={() => setShowModal(false)}
 *            footer={<button className="btn danger" onClick={handleDelete}>Delete</button>}>
 *       <p>Are you sure you want to delete this vendor?</p>
 *     </Modal>
 *   )}
 */

import { useEffect, useCallback } from "react";
import { X } from "lucide-react";

const WIDTH_MAP = { narrow: 440, wide: 960 };

export default function Modal({ title, children, footer, onClose, narrow, width }) {
  /* Close on Escape key */
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose?.();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  /* Prevent body scroll while modal is open */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div
        style={{
          ...styles.dialog,
          maxWidth: WIDTH_MAP[width] ?? (narrow ? 440 : 600),
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Header */}
        <div style={styles.header}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 650 }}>{title}</h2>
          <button
            onClick={onClose}
            style={styles.closeBtn}
            aria-label="Close dialog"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>{children}</div>

        {/* Footer */}
        {footer && <div style={styles.footer}>{footer}</div>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 8000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(0, 0, 0, 0.38)",
    backdropFilter: "blur(2px)",
    padding: 16,
  },
  dialog: {
    width: "100%",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    background: "var(--panel)",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius)",
    boxShadow: "var(--shadow-lg)",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    padding: "14px 18px",
    borderBottom: "1px solid var(--line-2)",
    flexShrink: 0,
  },
  closeBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "var(--ink-3)",
    padding: 4,
    display: "inline-flex",
    borderRadius: "var(--radius-sm)",
  },
  body: {
    padding: 18,
    overflowY: "auto",
    flex: 1,
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    padding: "12px 18px",
    borderTop: "1px solid var(--line-2)",
    flexShrink: 0,
  },
};
